import React, { useState, useEffect, useCallback } from 'react';

// Saved searches — an agent's standing hunt on MLS Opportunities.
//
// Two pieces, used together on the MLS tab:
//   <SaveSearchButton>  in the filter rail — turns whatever is set right now into a saved search
//   <SavedSearchesPanel> a drawer listing them all, with unread badges, that applies one on click
//
// Everything here is per-user: the API keys every read and write on the session's own user id, so a
// saved search is never visible to a colleague or an org owner.
//
// The criteria sent up are the SAME shape the rail holds. backend/lib/savedSearchMatch.js normalizes
// and then owns what they mean — that module is the authority, and the client never re-implements the
// match; it only sends what the agent set and renders what the server says matched.

// The rail's filter state → the saved-search criteria shape. Blank fields are dropped by the server's
// normalizer, so passing '' through is fine and keeps this a plain translation.
//
// ⚠️ Every narrowing control on the page has to appear here. `priceReducedOnly` and `typeFilter` are
// NOT inside the `filters` object — they are separate pieces of state on MlsOpportunities — so they
// have to be passed in by name, and both were silently dropped until a review caught it: an agent
// could set "Price Reduced", save, and get a search that matched everything. verify-saved-searches.js
// now asserts this function covers every key the server's normalizer accepts.
export function criteriaFromFilters({ filters, statusFilter, mode, priceReducedOnly, typeFilter }) {
  const f = filters || {};
  return {
    priceMin: f.priceMin, priceMax: f.priceMax,
    bedsMin: f.bedsMin, bathsMin: f.bathsMin,
    sqftMin: f.sqftMin, sqftMax: f.sqftMax,
    lotMin: f.lotMin, lotMax: f.lotMax,
    yearMin: f.yearMin, yearMax: f.yearMax,
    domMin: f.domMin, domMax: f.domMax,
    garageMin: f.garageMin, garageMax: f.garageMax,
    scoreMin: f.scoreMin, distressMin: f.distressMin,
    pool: f.pool || undefined,
    cashOnly: !!f.cashOnly,
    priceReducedOnly: !!priceReducedOnly,
    typeFilter: typeFilter && typeFilter !== 'all' ? typeFilter : undefined,
    zips: f.zips || [],
    propertyTypes: f.propertyTypes || [],
    // 'All' means the agent deliberately stopped narrowing by status — save that as "any status"
    // rather than pinning the search to whatever pill happened to be lit.
    statuses: statusFilter && statusFilter !== 'All' ? [statusFilter] : [],
    opportunitiesOnly: mode !== 'all',
  };
}

// Criteria → the rail's filter state, for applying a saved search back onto the screen.
export function filtersFromCriteria(criteria) {
  const c = criteria || {};
  const s = (v) => (v == null ? '' : String(v));
  return {
    filters: {
      priceMin: s(c.priceMin), priceMax: s(c.priceMax),
      bedsMin: s(c.bedsMin), bathsMin: s(c.bathsMin),
      sqftMin: s(c.sqftMin), sqftMax: s(c.sqftMax),
      lotMin: s(c.lotMin), lotMax: s(c.lotMax),
      yearMin: s(c.yearMin), yearMax: s(c.yearMax),
      domMin: s(c.domMin), domMax: s(c.domMax),
      garageMin: s(c.garageMin), garageMax: s(c.garageMax),
      scoreMin: s(c.scoreMin), distressMin: s(c.distressMin),
      pool: c.pool || '',
      cashOnly: !!c.cashOnly,
      closeWithin: '',
      zips: c.zips || [],
      propertyTypes: c.propertyTypes || [],
    },
    statusFilter: (c.statuses && c.statuses[0]) || 'All',
    mode: c.opportunitiesOnly === false ? 'all' : 'opportunities',
    // Restored alongside the rail so applying a saved search puts the screen back the way it was
    // when it was saved — not just the parts that happened to live in `filters`.
    priceReducedOnly: !!c.priceReducedOnly,
    typeFilter: c.typeFilter || 'all',
  };
}

const money = (n) => (n == null ? '—' : n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`);

// ── Save button — lives in the filter rail ───────────────────────────────────
export function SaveSearchButton({ criteria, activeCount, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);   // { name, matching_now, matching_now_capped }

  // Nothing set means a search that would match the whole market — the server refuses it, and the
  // button has nothing to offer, so it simply isn't there.
  if (!activeCount) return null;

  async function save() {
    if (!name.trim()) { setError('Give it a name.'); return; }
    setSaving(true); setError(null);
    try {
      const r = await fetch('/api/saved-searches', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), criteria }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Could not save.'); setSaving(false); return; }
      setSaved(d); setSaving(false);
      if (onSaved) onSaved(d);
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <>
      <button onClick={() => { setSaved(null); setName(''); setError(null); setOpen(true); }} style={{
        width: '100%', padding: '7px 10px', marginTop: 8, borderRadius: 8,
        background: 'var(--gold-dim)', border: '1px solid rgba(224,165,38,0.35)',
        color: '#8a6314', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
      }}>
        ☆ Save this search
      </button>

      {open && saved && (
        <div style={overlay} onClick={() => setOpen(false)}>
          <div style={{ ...panel, width: 'min(440px, 100%)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 20px 4px' }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Saved — “{saved.name}”</div>
              <div style={{ fontSize: 13, color: 'var(--gray-300)', marginTop: 6, lineHeight: 1.5 }}>
                {saved.matching_now > 0
                  ? <><b>{saved.matching_now.toLocaleString()}{saved.matching_now_capped ? '+' : ''}</b> listing{saved.matching_now === 1 ? '' : 's'} match it right now. </>
                  : <>Nothing on the market matches it yet. </>}
                You'll be told when a new one comes on, or a price cut brings one into range.
              </div>
            </div>
            <div style={foot}>
              <button onClick={() => setOpen(false)} style={primary}>Done</button>
            </div>
          </div>
        </div>
      )}

      {open && !saved && (
        <div style={overlay} onClick={() => setOpen(false)}>
          <div style={{ ...panel, width: 'min(440px, 100%)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 20px 6px' }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Save this search</div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.5 }}>
                We'll tell you when a listing comes on the market matching it — and when a price cut
                brings one into range.
              </div>
            </div>
            <div style={{ padding: '12px 20px 4px' }}>
              <label style={lbl}>Name</label>
              <input autoFocus value={name} onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') save(); }}
                placeholder="e.g. Glendale 3/2 under $350k" style={inp} />
              <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--navy)', border: '1px solid var(--navy-border)' }}>
                <div style={{ ...lbl, marginBottom: 4 }}>What it covers</div>
                <div style={{ fontSize: 12.5, color: 'var(--gray-100)', lineHeight: 1.5 }}>
                  {summarize(criteria)}
                </div>
              </div>
              {error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10, fontWeight: 600 }}>{error}</div>}
            </div>
            <div style={foot}>
              <button onClick={() => setOpen(false)} style={ghost}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ ...primary, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save search'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Client-side echo of describeCriteria() for the confirm dialog only — the server's version is what
// gets stored and shown in the list, so this never becomes the source of truth for anything.
function summarize(c) {
  const p = [];
  const n = (v) => (v === '' || v == null ? null : Number(v));
  if (c.statuses?.length) p.push(c.statuses.join(' / '));
  const pmin = n(c.priceMin), pmax = n(c.priceMax);
  if (pmin != null && pmax != null) p.push(`${money(pmin)}–${money(pmax)}`);
  else if (pmin != null) p.push(`${money(pmin)}+`);
  else if (pmax != null) p.push(`under ${money(pmax)}`);
  if (n(c.bedsMin) != null) p.push(`${n(c.bedsMin)}+ bd`);
  if (n(c.bathsMin) != null) p.push(`${n(c.bathsMin)}+ ba`);
  const smin = n(c.sqftMin), smax = n(c.sqftMax);
  if (smin != null && smax != null) p.push(`${smin.toLocaleString()}–${smax.toLocaleString()} sqft`);
  else if (smin != null) p.push(`${smin.toLocaleString()}+ sqft`);
  else if (smax != null) p.push(`under ${smax.toLocaleString()} sqft`);
  if (c.pool === 'yes') p.push('pool');
  if (c.pool === 'no') p.push('no pool');
  if (c.cashOnly) p.push('cash');
  if (c.priceReducedOnly) p.push('price reduced');
  if (c.typeFilter) p.push({ lots: 'lots & scrapes', new: 'new to market', well_priced: 'well-priced', hot: 'hot market' }[c.typeFilter] || c.typeFilter);
  if (c.propertyTypes?.length) p.push(c.propertyTypes.join(' / '));
  if (c.zips?.length) p.push(c.zips.length > 4 ? `${c.zips.length} zips` : c.zips.join(', '));
  return p.join(' · ') || 'Everything';
}

// ── The drawer — all saved searches in one place ─────────────────────────────
export function SavedSearchesPanel({ onClose, onApply, reloadKey }) {
  const [searches, setSearches] = useState([]);
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);      // which search's matches are expanded

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch('/api/saved-searches').then(r => r.json()),
      fetch('/api/saved-searches/hits?unseen=1&limit=100').then(r => r.json()),
    ]).then(([s, h]) => {
      setSearches(Array.isArray(s.searches) ? s.searches : []);
      setHits(Array.isArray(h.hits) ? h.hits : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  useEffect(load, [load, reloadKey]);

  async function remove(id) {
    if (!window.confirm('Delete this saved search? Its alerts go with it.')) return;
    await fetch(`/api/saved-searches/${id}`, { method: 'DELETE' });
    load();
  }
  async function toggleNotify(s) {
    await fetch(`/api/saved-searches/${s.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notify: !s.notify }),
    });
    load();
  }
  async function markSeen(searchId) {
    await fetch('/api/saved-searches/hits/seen', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(searchId ? { search_id: searchId } : {}),
    });
    load();
  }

  const hitsFor = (id) => hits.filter(h => h.saved_search_id === id);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...panel, width: 'min(760px, 100%)', maxHeight: '86vh' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px', borderBottom: '1px solid var(--navy-border)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Saved searches</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>
              {hits.length
                ? `${hits.length} new match${hits.length === 1 ? '' : 'es'} since you last looked.`
                : 'You’ll be alerted when a new listing matches, or a price cut brings one into range.'}
            </div>
          </div>
          {hits.length > 0 && (
            <button onClick={() => markSeen(null)} style={ghostSm}>Mark all read</button>
          )}
          <button onClick={onClose} style={closeX} aria-label="Close">×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={empty}>Loading…</div>
          ) : !searches.length ? (
            <div style={{ ...empty, lineHeight: 1.6 }}>
              No saved searches yet.<br />
              <span style={{ color: 'var(--gray-500)', fontSize: 12.5 }}>
                Set the filters the way you hunt, then hit <b>☆ Save this search</b> in the filter rail.
              </span>
            </div>
          ) : searches.map(s => {
            const mine = hitsFor(s.id);
            const isOpen = openId === s.id;
            return (
              <div key={s.id} style={{ borderBottom: '1px solid var(--navy-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 20px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</span>
                      {s.unseen > 0 && (
                        <span style={badge}>{s.unseen} new</span>
                      )}
                      {!s.notify && <span style={mutedTag}>alerts off</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.summary}
                    </div>
                  </div>
                  <button onClick={() => { setOpenId(isOpen ? null : s.id); if (!isOpen && s.unseen) markSeen(s.id); }}
                    style={ghostSm}>
                    {isOpen ? 'Hide' : mine.length ? `View ${mine.length}` : 'Matches'}
                  </button>
                  <button onClick={() => { onApply(s.criteria); onClose(); }} style={{ ...ghostSm, color: 'var(--teal)', borderColor: 'rgba(20,80,74,0.35)' }}>
                    Apply
                  </button>
                  <button onClick={() => toggleNotify(s)} title={s.notify ? 'Turn alerts off' : 'Turn alerts on'} style={iconBtn}>
                    {s.notify ? '🔔' : '🔕'}
                  </button>
                  <button onClick={() => remove(s.id)} title="Delete" style={{ ...iconBtn, color: 'var(--red)' }}>×</button>
                </div>

                {isOpen && <Matches searchId={s.id} newHits={mine} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Expanded row: the new alerts first (they're why you opened it), then everything that matches today.
function Matches({ searchId, newHits }) {
  // `listings` is the first page; `count` is how many actually match. They differ — the endpoint caps
  // the page at 300 — so rendering listings.length as the total would report 300 for a search
  // matching 544.
  const [res, setRes] = useState(null);
  useEffect(() => {
    let dead = false;
    fetch(`/api/saved-searches/${searchId}/matches`)
      .then(r => r.json())
      .then(d => { if (!dead) setRes({ listings: Array.isArray(d.listings) ? d.listings : [], count: d.count || 0, capped: !!d.count_capped }); })
      .catch(() => { if (!dead) setRes({ listings: [], count: 0, capped: false }); });
    return () => { dead = true; };
  }, [searchId]);
  const listings = res?.listings ?? null;

  return (
    <div style={{ background: 'var(--navy)', padding: '4px 20px 14px' }}>
      {newHits.length > 0 && (
        <>
          <div style={{ ...lbl, padding: '10px 0 6px' }}>New since you last looked</div>
          {newHits.map(h => (
            <div key={h.id} style={hitRow}>
              <span style={{
                ...pill,
                background: h.reason === 'new' ? 'var(--teal-dim)' : 'rgba(184,84,80,0.12)',
                color: h.reason === 'new' ? 'var(--teal)' : 'var(--red)',
              }}>
                {h.reason === 'new' ? 'New listing' : h.newly_matching ? 'Now in range' : 'Price cut'}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {h.address}
              </span>
              <span style={{ fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {h.reason === 'price_drop'
                  ? <><s>{money(h.old_price)}</s> → <b style={{ color: 'var(--white)' }}>{money(h.new_price)}</b></>
                  : money(h.new_price)}
              </span>
            </div>
          ))}
        </>
      )}

      <div style={{ ...lbl, padding: '12px 0 6px' }}>
        Matching now {res ? `(${res.count.toLocaleString()}${res.capped ? '+' : ''})` : ''}
      </div>
      {listings === null ? (
        <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '6px 0' }}>Checking the market…</div>
      ) : !listings.length ? (
        <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '6px 0' }}>
          Nothing on the market matches this right now — you'll be told the moment something does.
        </div>
      ) : listings.slice(0, 12).map(l => (
        <div key={l.listing_key} style={hitRow}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {l.address}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
            {[l.beds && `${l.beds}bd`, l.baths && `${l.baths}ba`, l.sqft && `${Number(l.sqft).toLocaleString()} sqft`].filter(Boolean).join(' · ')}
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{money(l.list_price)}</span>
        </div>
      ))}
      {res && res.count > 12 && (
        <div style={{ fontSize: 11.5, color: 'var(--gray-500)', paddingTop: 6 }}>
          + {(res.count - 12).toLocaleString()}{res.capped ? '+' : ''} more — hit <b>Apply</b> to see them all on the board.
        </div>
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const overlay = { position: 'fixed', inset: 0, background: 'rgba(26,23,20,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 20 };
const panel = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, display: 'flex', flexDirection: 'column', color: 'var(--white)', boxShadow: '0 20px 60px rgba(20,30,26,0.25)', maxHeight: '86vh' };
const foot = { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--navy-border)', marginTop: 12 };
const lbl = { fontSize: 10.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.07em' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, fontFamily: 'inherit', marginTop: 5 };
const ghost = { padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' };
const ghostSm = { padding: '5px 11px', borderRadius: 7, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 };
const primary = { padding: '8px 16px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' };
const iconBtn = { background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', padding: '2px 4px', color: 'var(--gray-500)', lineHeight: 1, flexShrink: 0 };
const closeX = { background: 'transparent', border: 'none', color: 'var(--gray-500)', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 0, flexShrink: 0 };
const badge = { background: 'var(--gold)', color: '#3A2C08', fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' };
const mutedTag = { background: 'var(--navy-light)', color: 'var(--gray-500)', fontSize: 10, fontWeight: 600, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' };
const pill = { fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 };
const hitRow = { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(228,222,208,0.5)' };
const empty = { padding: 40, textAlign: 'center', color: 'var(--gray-300)', fontSize: 13.5 };
